import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  CircularProgress,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import InventoryIcon from "@mui/icons-material/Inventory";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import DataGridViewport from "../components/DataGridViewport";
import { useAppConfig } from "../hooks/AppConfigContext";
import { useUserContext } from "../hooks/UserContext";
import LocalSelector from "./components/LocalSelector";
import { fetchGetCatalogosModificacionPrecios, ProductoCatalogoItem } from "../services/productoService";
import { fetchExportReporteStockPorLocalTodo, fetchGetReporteStockPorLocal, ReporteStockPorLocalItem } from "../services/reporteService";
import { isAbortError } from "../services/fetchService";
import { CurrencyFormatter } from "../utils/CurrencyFormatter";

const toMoney = (value: number | undefined) => CurrencyFormatter.format(Number(value ?? 0));
const toNumber = (value: number | undefined) => Number(value ?? 0).toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 4 });

const ReporteStockPorLocal = () => {
  const { getUser } = useUserContext();
  const { productoVentaHeaders } = useAppConfig();
  const user = useMemo(() => getUser(), [getUser]);
  const [idLocal, setIdLocal] = useState<string | undefined>(undefined);
  const [rubros, setRubros] = useState<ProductoCatalogoItem[]>([]);
  const [idRubro, setIdRubro] = useState<number | undefined>(undefined);
  const [search, setSearch] = useState("");
  const [searchToApply, setSearchToApply] = useState("");
  const [incluirDesactivados, setIncluirDesactivados] = useState(false);
  const [comparadorStock, setComparadorStock] = useState("");
  const [cantidadStock, setCantidadStock] = useState("");
  const [items, setItems] = useState<ReporteStockPorLocalItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });

  const contadoHeader = productoVentaHeaders.CONTADO?.Header ?? "Contado";
  const mayorHeader = productoVentaHeaders.MAYOR?.Header ?? "Mayor";
  const menorHeader = productoVentaHeaders.MENOR?.Header ?? "Menor";

  useEffect(() => {
    if (!user) return;
    const controller = new AbortController();
    fetchGetCatalogosModificacionPrecios(user, undefined, controller.signal)
      .then((catalogos) => setRubros(catalogos.rubros))
      .catch((error) => {
        if (!isAbortError(error)) setRubros([]);
      });

    return () => controller.abort();
  }, [user]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setPaginationModel((current) => ({ ...current, page: 0 }));
      setSearchToApply(search);
    }, 350);

    return () => window.clearTimeout(timeoutId);
  }, [search]);

  useEffect(() => {
    if (!user || !idLocal) {
      setItems([]);
      setTotal(0);
      return;
    }

    const controller = new AbortController();
    setLoading(true);
    setError(null);
    fetchGetReporteStockPorLocal(user, idLocal, paginationModel.page, paginationModel.pageSize, {
      search: searchToApply,
      idRubro,
      incluirDesactivados,
      comparadorStock,
      cantidadStock: cantidadStock.trim() ? Number(cantidadStock.replace(",", ".")) : undefined,
    }, controller.signal)
      .then((data) => {
        setItems(data.items);
        setTotal(data.total);
      })
      .catch((error) => {
        if (isAbortError(error)) return;
        setItems([]);
        setTotal(0);
        setError("No se pudo cargar el reporte de stock.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [cantidadStock, comparadorStock, idLocal, idRubro, incluirDesactivados, paginationModel.page, paginationModel.pageSize, refreshKey, searchToApply, user]);

  const columns = useMemo<GridColDef[]>(() => [
    { field: "codigo", headerName: "Codigo", minWidth: 120, flex: 0.5 },
    { field: "producto", headerName: "Producto", minWidth: 260, flex: 1.3 },
    { field: "stock", headerName: "Stock", minWidth: 120, align: "right", headerAlign: "right", valueFormatter: ({ value }) => toNumber(value) },
    { field: "pendiente", headerName: "Pendiente", minWidth: 120, align: "right", headerAlign: "right", valueGetter: () => 0, valueFormatter: ({ value }) => toNumber(value) },
    { field: "precioContado", headerName: contadoHeader, minWidth: 130, align: "right", headerAlign: "right", valueFormatter: ({ value }) => toMoney(value) },
    { field: "precioMayor", headerName: mayorHeader, minWidth: 130, align: "right", headerAlign: "right", valueFormatter: ({ value }) => toMoney(value) },
    { field: "precioMenor", headerName: menorHeader, minWidth: 130, align: "right", headerAlign: "right", valueFormatter: ({ value }) => toMoney(value) },
    { field: "precioCompra", headerName: "Precio compra", minWidth: 140, align: "right", headerAlign: "right", valueFormatter: ({ value }) => toMoney(value) },
    { field: "rubro", headerName: "Rubro", minWidth: 160 },
    { field: "estado", headerName: "Activo", minWidth: 100 },
  ], [contadoHeader, mayorHeader, menorHeader]);

  const rows = useMemo(() => items.map((item) => ({ ...item, id: item.idProducto })), [items]);

  const exportarTodo = () => {
    if (!user || !idLocal) {
      return;
    }

    setExporting(true);
    fetchExportReporteStockPorLocalTodo(user, idLocal, incluirDesactivados)
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `stock-local-${idLocal}.csv`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => setError("No se pudo exportar el reporte de stock."))
      .finally(() => setExporting(false));
  };

  return (
    <Box sx={{ py: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <InventoryIcon color="primary" />
        <Typography variant="h4">Stock por local</Typography>
      </Box>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={2.5}>
            <LocalSelector onLocalChange={setIdLocal} />
          </Grid>
          <Grid item xs={12} md={2.5}>
            <TextField label="Buscar codigo/producto" value={search} onChange={(event) => setSearch(event.target.value)} fullWidth />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Rubro</InputLabel>
              <Select value={idRubro ?? ""} label="Rubro" onChange={(event) => { setIdRubro(event.target.value ? Number(event.target.value) : undefined); setPaginationModel((current) => ({ ...current, page: 0 })); }}>
                <MenuItem value="">Todos</MenuItem>
                {rubros.map((item) => <MenuItem key={item.id} value={item.id}>{item.nombre}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={1.5}>
            <FormControl fullWidth>
              <InputLabel>Stock</InputLabel>
              <Select value={comparadorStock} label="Stock" onChange={(event) => { setComparadorStock(event.target.value); setPaginationModel((current) => ({ ...current, page: 0 })); }}>
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="igual">Igual</MenuItem>
                <MenuItem value="mayor">Mayor</MenuItem>
                <MenuItem value="menor">Menor</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={1.5}>
            <TextField label="Cantidad" type="number" value={cantidadStock} onChange={(event) => { setCantidadStock(event.target.value); setPaginationModel((current) => ({ ...current, page: 0 })); }} fullWidth disabled={!comparadorStock} />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Button variant="outlined" startIcon={loading ? <CircularProgress color="inherit" size={18} /> : <RefreshIcon />} onClick={() => setRefreshKey((current) => current + 1)} disabled={loading} fullWidth sx={{ whiteSpace: "nowrap" }}>
              Actualizar
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Button variant="outlined" startIcon={exporting ? <CircularProgress color="inherit" size={18} /> : <DownloadIcon />} onClick={exportarTodo} disabled={exporting || !idLocal} fullWidth sx={{ whiteSpace: "nowrap" }}>
              Exportar todo
            </Button>
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={<Checkbox checked={incluirDesactivados} onChange={(event) => { setIncluirDesactivados(event.target.checked); setPaginationModel((current) => ({ ...current, page: 0 })); }} />}
              label="Incluir productos desactivados"
            />
          </Grid>
        </Grid>
      </Paper>

      {error ? (
        <Alert severity="error">{error}</Alert>
      ) : idLocal ? (
        <DataGridViewport height={650} minWidth={1180}>
          <DataGrid
            rows={rows}
            columns={columns}
            rowCount={total}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 20, 50, 100]}
            loading={loading}
            disableRowSelectionOnClick
          />
        </DataGridViewport>
      ) : (
        <Alert severity="info">Seleccione un local para consultar stock.</Alert>
      )}
    </Box>
  );
};

export default ReporteStockPorLocal;
